import React from 'react';
import { styled } from '@mui/material/styles';
import { Dialog, DialogProps, Paper, PaperProps, Slide } from '@mui/material';
import { useResponsive } from 'src/hooks/useResponsive';
import { CONTENT_MAX_WIDTH, DIALOG_DESKTOP_MAX_WIDTH } from 'config/constants';

const StyledPaper = styled(Paper, {
    shouldForwardProp: prop => prop !== 'isDesktop' && prop !== 'isFullScreen'
})<{ isDesktop: boolean; isFullScreen: boolean }>(({ theme, isDesktop, isFullScreen }) => ({
    margin: 0,
    position: 'fixed',
    maxWidth: CONTENT_MAX_WIDTH,
    padding: theme.spacing(1),
    height: isFullScreen ? '100%' : undefined,
    ...(!isDesktop
        ? {
              width: '100%',
              bottom: 0
          }
        : {
              width: 'auto',
              maxWidth: DIALOG_DESKTOP_MAX_WIDTH
          })
}));

export interface IBottomDialogProps {
    open: boolean;
    fullScreen?: boolean;
    onClose?: () => void;
    className?: string;
    /** @deprecated : use paperProps instead */
    paperClassName?: string;
    disableBackdropClick?: boolean;
    disableEnforceFocus?: boolean;
    paperProps?: PaperProps;
    onEntered?: (node: HTMLElement, isAppearing: boolean) => void;
    sx?: DialogProps['sx'];
}

export const BottomDialog: React.FC<React.PropsWithChildren<IBottomDialogProps>> = props => {
    const { isDesktop } = useResponsive();
    const {
        onClose,
        open,
        fullScreen,
        paperClassName,
        paperProps,
        disableBackdropClick,
        disableEnforceFocus,
        children,
        onEntered,
        sx
    } = props;

    const handleClose = React.useCallback(() => {
        if (!disableBackdropClick && onClose) {
            onClose();
        }
    }, [disableBackdropClick, onClose]);

    const renderCustomPaper = React.useCallback(
        (props: PaperProps) => (
            <StyledPaper
                {...props}
                isDesktop={isDesktop}
                className={paperClassName ?? undefined}
                isFullScreen={!!fullScreen}
            />
        ),
        [isDesktop, paperClassName, fullScreen]
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth={false}
            slotProps={{
                transition: {
                    direction: 'up',
                    onEntered
                },
                paper: paperProps
            }}
            slots={{
                transition: Slide,
                paper: renderCustomPaper
            }}
            fullScreen={fullScreen}
            disableEnforceFocus={disableEnforceFocus}
            sx={sx}
        >
            {children}
        </Dialog>
    );
};
